import { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  ArrowLeft,
  Package,
  Calendar,
  MapPin,
  Clock,
  CheckCircle2,
  User,
  Phone,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface WarehouseOrder {
  id: string;
  order_number: number;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  pickup_date?: string;
  pickup_time?: string;
  pickup_location?: string;
  observations?: string;
  status: string;
  payment_status: string;
  warehouse_status?: string;
  total: number;
  created_at: string;
  locations?: { id: string; code: string; name: string } | null;
  order_items?: Array<{
    id: string;
    product_name: string;
    quantity: number;
    unit_price: number;
    removed_ingredients?: string[] | null;
  }>;
}

interface WarehouseDashboardProps {
  token: string;
  onBack: () => void;
}

const WarehouseDashboard = ({ token, onBack }: WarehouseDashboardProps) => {
  const { toast } = useToast();
  const [orders, setOrders] = useState<WarehouseOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"pending" | "ready">("pending");

  // Fetch all encargo orders with status 'en_almacen' (sent from stores)
  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("admin-orders", {
        body: { 
          action: "fetch-warehouse",
          token,
        },
      });

      if (error || !data?.success) {
        console.error("Error fetching warehouse orders:", error || data?.error);
        toast({ title: "Error al cargar encargos", variant: "destructive" });
        return;
      }

      setOrders(data.orders || []);
    } catch (error) {
      console.error("Warehouse fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    
    // Poll every 30 seconds
    const interval = setInterval(fetchOrders, 30000);
    
    return () => clearInterval(interval);
  }, [token]);

  // Filter orders: pending = status 'en_almacen', ready = status 'completado'
  const pendingOrders = useMemo(() => 
    orders.filter(o => o.status === "en_almacen")
      .sort((a, b) => {
        // Sort by pickup_date (soonest first)
        const dateA = a.pickup_date ? new Date(a.pickup_date).getTime() : Infinity;
        const dateB = b.pickup_date ? new Date(b.pickup_date).getTime() : Infinity;
        return dateA - dateB;
      })
  , [orders]);

  const readyOrders = useMemo(() => 
    orders.filter(o => o.status === "completado")
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  , [orders]);

  // Mark order as ready (change status to 'completado')
  const handleMarkReady = async (orderId: string) => {
    setProcessingId(orderId);
    
    try {
      const { data, error } = await supabase.functions.invoke("admin-orders", {
        body: {
          action: "update-status",
          token,
          orderId,
          newStatus: "completado",
        },
      });

      if (error || !data?.success) {
        throw new Error(data?.error || "Error al actualizar");
      }

      // Optimistic update
      setOrders(prev => prev.map(o => 
        o.id === orderId ? { ...o, status: "completado" } : o
      ));
      
      toast({ 
        title: "✅ Encargo listo", 
        description: "El pedido aparecerá como completado en la tienda" 
      });
    } catch (error) {
      console.error("Error updating order status:", error);
      toast({ title: "Error al actualizar", variant: "destructive" });
    } finally {
      setProcessingId(null);
    }
  };

  const formatPickupDate = (dateStr?: string) => {
    if (!dateStr) return "Sin fecha";
    try {
      return format(new Date(dateStr), "EEEE d 'de' MMMM", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const getUrgencyBadge = (pickupDate?: string) => {
    if (!pickupDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pickup = new Date(pickupDate);
    pickup.setHours(0, 0, 0, 0);
    
    const diffDays = Math.ceil((pickup.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) {
      return <Badge className="bg-red-500 text-white animate-pulse">🔴 HOY</Badge>;
    } else if (diffDays === 1) {
      return <Badge className="bg-orange-500 text-white">🟠 MAÑANA</Badge>;
    } else if (diffDays <= 3) {
      return <Badge className="bg-yellow-500 text-black">🟡 {diffDays} días</Badge>;
    }
    return <Badge variant="outline">{diffDays} días</Badge>;
  };

  const renderOrderCard = (order: WarehouseOrder, isReady: boolean) => (
    <Card 
      key={order.id} 
      className={`mb-4 border-2 ${
        isReady 
          ? "bg-green-50 border-green-300" 
          : "bg-purple-50 border-purple-300"
      }`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-2xl font-bold">#{order.order_number}</div>
            {getUrgencyBadge(order.pickup_date)}
            {isReady && (
              <Badge className="bg-green-500 text-white">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                COMPLETADO
              </Badge>
            )}
            {!isReady && (
              <Badge className="bg-purple-500 text-white">
                <Package className="h-3 w-3 mr-1" />
                EN ELABORACIÓN
              </Badge>
            )}
          </div>
          <Badge variant="outline" className="text-lg">
            {order.total.toFixed(2)}€
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Pickup Info - PROMINENTE */}
        <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
          <div className="flex items-center gap-2 text-primary font-semibold mb-2">
            <Calendar className="h-5 w-5" />
            FECHA DE RECOGIDA
          </div>
          <div className="text-2xl font-bold capitalize">
            {formatPickupDate(order.pickup_date)}
          </div>
          {order.pickup_time && (
            <div className="flex items-center gap-2 mt-2 text-lg">
              <Clock className="h-4 w-4" />
              {order.pickup_time}
            </div>
          )}
        </div>
        
        {/* Destination Store */}
        <div className="flex items-center gap-2 bg-blue-50 p-3 rounded-lg">
          <MapPin className="h-5 w-5 text-blue-600" />
          <span className="font-semibold text-blue-800">
            {order.locations?.name || order.pickup_location || "Sin sede asignada"}
          </span>
        </div>
        
        {/* Customer Info */}
        {(order.customer_name || order.customer_phone) && (
          <div className="flex flex-wrap gap-3 text-sm">
            {order.customer_name && (
              <div className="flex items-center gap-1">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{order.customer_name}</span>
              </div>
            )}
            {order.customer_phone && (
              <div className="flex items-center gap-1">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{order.customer_phone}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Products List */}
        <div className="border rounded-lg p-3 bg-muted/30">
          <div className="font-semibold mb-2 flex items-center gap-2">
            <Package className="h-4 w-4" />
            Productos ({order.order_items?.length || 0})
          </div>
          <div className="space-y-2">
            {order.order_items?.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <div>
                  <span className="font-medium">{item.quantity}x</span>{" "}
                  {item.product_name}
                  {item.removed_ingredients && item.removed_ingredients.length > 0 && (
                    <div className="text-orange-600 text-xs font-semibold ml-4">
                      ⚠️ {item.removed_ingredients.map(i => `Sin ${i}`).join(", ")}
                    </div>
                  )}
                </div>
                <span className="text-muted-foreground">
                  {(item.quantity * item.unit_price).toFixed(2)}€
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Observations */}
        {order.observations && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="font-semibold text-yellow-800 mb-1">📝 Notas del cliente:</div>
            <div className="text-sm text-yellow-900">{order.observations}</div>
          </div>
        )}
        
        {/* Action Button */}
        {!isReady && (
          <Button 
            className="w-full h-14 text-lg font-semibold bg-green-600 hover:bg-green-700"
            onClick={() => handleMarkReady(order.id)}
            disabled={processingId === order.id}
          >
            {processingId === order.id ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-5 w-5 mr-2" />
                ✅ Confirmar Elaboración / Listo
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 p-4">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={onBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Salir
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6" />
            Panel Almacén / Obrador
          </h1>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-4xl mx-auto">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "pending" | "ready")}>
          <TabsList className="w-full mb-6">
            <TabsTrigger value="pending" className="flex-1 gap-2">
              📦 Por Elaborar
              {pendingOrders.length > 0 && (
                <Badge variant="destructive">{pendingOrders.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="ready" className="flex-1 gap-2">
              ✅ Completados
              {readyOrders.length > 0 && (
                <Badge variant="secondary">{readyOrders.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="pending">
            {pendingOrders.length === 0 ? (
              <Card className="p-8 text-center">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-xl font-semibold">¡Todo al día!</h3>
                <p className="text-muted-foreground">No hay encargos pendientes de elaborar</p>
              </Card>
            ) : (
              pendingOrders.map(order => renderOrderCard(order, false))
            )}
          </TabsContent>
          
          <TabsContent value="ready">
            {readyOrders.length === 0 ? (
              <Card className="p-8 text-center">
                <div className="text-4xl mb-4">📦</div>
                <p className="text-muted-foreground">Aún no hay encargos completados hoy</p>
              </Card>
            ) : (
              readyOrders.map(order => renderOrderCard(order, true))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default WarehouseDashboard;